<!--
@component
AccessibleBracket

Visually-hidden table fallback for screen readers and search engines.
Renders one `<table>` per knockout round (including the third-place final
when present), reusing the shared `getMatchSummary`/`getWinnerLabel`
helpers so labels stay in sync with the visual bracket.
-->

<script lang="ts">
  import { Block } from '@reuters-graphics/graphics-components';
  import type { Venue } from '$utils/getMatchStats';
  import {
    formatScoreText,
    getMatchSummary,
    getWinnerLabel,
    type Round,
  } from '../content';

  let {
    matches,
    thirdPlaceMatch = null,
    venues,
  }: {
    matches: Round[];
    /** Already shaped as a round (`{ round, matches }`), not a bare match. */
    thirdPlaceMatch?: Round | null;
    venues: Venue[];
  } = $props();

  /** All knockout rounds, including the third-place final when present. */
  let rounds = $derived<Round[]>(
    thirdPlaceMatch ? [...matches, thirdPlaceMatch] : matches
  );
</script>

<Block width="wider" class="visually-hidden">
  {#each rounds as round (round.round)}
    <table
      class="text-center text-sm"
      role="region"
      aria-label="Knockout bracket"
    >
      <caption class="h2">{round.round}</caption>
      <thead>
        <tr>
          <th scope="col">Match</th>
          <th scope="col">Date</th>
          <th scope="col">Location</th>
          <th scope="col">Score</th>
          <th scope="col">Winner</th>
        </tr>
      </thead>
      <tbody>
        {#each round.matches as match, i (match.matchInfo?.id ?? i)}
          {@const summary = getMatchSummary(match, venues)}
          {@const matchDate = match?.localDateStamp ?? null}
          {@const iso = match?.isoDate ?? ''}
          <tr>
            <th scope="row">{summary.homeLabel} vs. {summary.awayLabel}</th>
            <td>
              {#if matchDate}
                <time datetime={iso}>
                  {matchDate.month}
                  {matchDate.day}, {matchDate.time}
                </time>
              {:else}
                –
              {/if}
            </td>
            <td>
              {[summary.venue?.name, summary.venue?.city]
                .filter(Boolean)
                .join(', ')}
            </td>
            <td>
              {#if summary.hasScore}
                {summary.homeLabel}: {formatScoreText(summary.homeScore)},
                {summary.awayLabel}: {formatScoreText(summary.awayScore)}
              {:else}
                –
              {/if}
            </td>
            <td>{getWinnerLabel(summary)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/each}
</Block>

<style lang="scss">
  table {
    width: 100%;
  }
</style>
